import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Item } from '../types';
import { formatGBP } from '../utils/currency';
import MessageThread from './MessageThread';
import Map from './Map';
import StarRating from './StarRating';
import './RentalDetails.css';

// Base64 encoded simple SVG placeholder
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';

interface Rental {
  item: Item;
  startDate: string;
  endDate: string;
  totalCost: number;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled';
  feedback?: {
    rating: number;
    comment: string;
    date: string;
  };
}

const RentalDetails: React.FC = () => {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rental, setRental] = useState<Rental | null>(null);
  const [loading, setLoading] = useState(true);
  const [ownerName, setOwnerName] = useState('John Smith'); // TODO: Replace with actual owner data

  useEffect(() => {
    if (user) {
      // TODO: Replace with actual API call
      // For now using mock data that matches MyRentals
      const mockRentals: Rental[] = [
        {
          item: {
            id: '1',
            name: 'Lawnmower',
            description: 'Powerful electric lawnmower',
            dailyRateGBP: 15.00,
            images: [PLACEHOLDER_IMAGE],
            ownerId: 'john-smith',
            minDays: 1,
            maxDays: 7,
            location: {
              address: '123 Garden Street',
              city: 'London',
              postcode: 'SW1A 1AA',
              coordinates: { latitude: 51.5074, longitude: -0.1278 }
            },
            category: 'Garden Tools',
            availability: true,
            rentalPeriods: []
          },
          startDate: '2024-04-01',
          endDate: '2024-04-03',
          totalCost: 4500, // 45.00 GBP in pence
          status: 'upcoming' as const
        },
        {
          item: {
            id: '2',
            name: 'Pressure Washer',
            description: 'High-pressure water cleaner',
            dailyRateGBP: 20.00,
            images: [PLACEHOLDER_IMAGE],
            ownerId: 'sarah-johnson',
            minDays: 1,
            maxDays: 5,
            location: {
              address: '45 Clean Street',
              city: 'London',
              postcode: 'SW2 8BQ',
              coordinates: { latitude: 51.5074, longitude: -0.1278 }
            },
            category: 'Cleaning Equipment',
            availability: true,
            rentalPeriods: []
          },
          startDate: '2024-03-15',
          endDate: '2024-03-16',
          totalCost: 2000, // 20.00 GBP in pence
          status: 'completed' as const,
          feedback: {
            rating: 4.5,
            comment: "Equipment was in great condition and the renter was very responsible. Would rent to them again!",
            date: '2024-03-17'
          }
        },
        {
          item: {
            id: '3',
            name: 'Hedge Trimmer',
            description: 'Cordless hedge trimmer with extended reach',
            dailyRateGBP: 18.00,
            images: [PLACEHOLDER_IMAGE],
            ownerId: 'mike-brown',
            minDays: 1,
            maxDays: 3,
            location: {
              address: '78 Garden Lane',
              city: 'London',
              postcode: 'N1 9RT',
              coordinates: { latitude: 51.5074, longitude: -0.1278 }
            },
            category: 'Garden Tools',
            availability: true,
            rentalPeriods: []
          },
          startDate: '2024-02-20',
          endDate: '2024-02-21',
          totalCost: 1800, // 18.00 GBP in pence
          status: 'cancelled' as const
        },
        {
          item: {
            id: '4',
            name: 'Leaf Blower',
            description: 'Powerful cordless leaf blower',
            dailyRateGBP: 12.00,
            images: [PLACEHOLDER_IMAGE],
            ownerId: 'sarah-johnson',
            minDays: 1,
            maxDays: 4,
            location: {
              address: '45 Clean Street',
              city: 'London',
              postcode: 'SW2 8BQ',
              coordinates: { latitude: 51.5074, longitude: -0.1278 }
            },
            category: 'Garden Tools',
            availability: true,
            rentalPeriods: []
          },
          startDate: '2024-03-01',
          endDate: '2024-03-02',
          totalCost: 1200, // 12.00 GBP in pence
          status: 'completed' as const,
          feedback: {
            rating: 5.0,
            comment: "Excellent renter! Very responsible and took great care of the equipment. Highly recommended!",
            date: '2024-03-03'
          }
        }
      ];

      // Find the rental that matches the rentalId (format: itemId-startDate)
      const foundRental = mockRentals.find(rental => 
        `${rental.item.id}-${rental.startDate}` === rentalId
      );

      if (foundRental) {
        setRental(foundRental);
      } else {
        // Fallback to first rental if no match found
        setRental(mockRentals[0]);
      }
      
      setLoading(false);
    }
  }, [user, rentalId]);

  if (!user) {
    return <div>Please log in to view rental details.</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!rental) {
    return <div>Rental not found.</div>;
  }

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel this rental?')) {
      // TODO: Implement cancel rental API call
      console.log('Cancelling rental:', rentalId);
    }
  };

  return (
    <div className="rental-details">
      <div className="rental-details-content">
        <div className="rental-details-header">
          <h1>{rental.item.name}</h1>
        </div>

        <div className="rental-details-grid">
          <div className="rental-details-left">
            <div className="rental-details-images">
              <img
                src={rental.item.images[0]}
                alt={rental.item.name}
                className="main-image"
              />
              <div className={`rental-status ${rental.status}`}>
                {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
              </div>
            </div>

            <div className="info-section">
              <h2>Item Details</h2>
              <p>{rental.item.description}</p>
              <p>Category: {rental.item.category}</p>
            </div>

            <div className="info-section">
              <h2>Rental Period</h2>
              <p>From: {new Date(rental.startDate).toLocaleDateString()}</p>
              <p>To: {new Date(rental.endDate).toLocaleDateString()}</p>
            </div>

            <div className="info-section">
              <h2>Cost</h2>
              <div className="cost-details">
                <div className="cost-breakdown">
                  <div className="cost-item">
                    <span>Total Cost:</span>
                    <span>{formatGBP(rental.totalCost)}</span>
                  </div>
                </div>
              </div>
            </div>

            {rental.status === 'completed' && rental.feedback && (
              <div className="info-section feedback-section">
                <h2>Owner's Feedback</h2>
                <div className="feedback-content">
                  <div className="feedback-rating">
                    <StarRating rating={rental.feedback.rating} totalRatings={1} size="medium" />
                  </div>
                  <p className="feedback-comment">{rental.feedback.comment}</p>
                  <p className="feedback-date">
                    Received on {new Date(rental.feedback.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            <div className="info-section">
              <h2>Pickup Location</h2>
              {rental.item.location ? (
                <>
                  <p>{rental.item.location.address || 'Address not available'}</p>
                  <p>
                    {rental.item.location.city || 'City not available'}, {rental.item.location.postcode || 'Postcode not available'}
                  </p>
                  <Map location={rental.item.location} />
                </>
              ) : (
                <p>Location not available</p>
              )}
            </div>

            {rental.status === 'upcoming' && (
              <button className="cancel-button" onClick={handleCancel}>
                Cancel Rental
              </button>
            )}
          </div>

          <div className="rental-details-right">
            <MessageThread
              recipientId={rental.item.ownerId}
              recipientName={ownerName}
              rentalId={rentalId || ''}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalDetails; 